/**
 * WikiLinkParser.ts - WikiLinkParser - Parses wiki-style links and tags from markdown content
 * 
 * CHANGE HISTORY:
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
export class WikiLinkParser {
    items: any[];
  getField: any;

  constructor(items, getField) {
    this.items = items;
    this.getField = getField;
  }
  
  /**
   * Parse wiki links from content
   * @param {string} content - Markdown content
   * @returns {Array} Array of link objects { title, alias, raw }
   */
  parseLinks(content): any {
    if (!content) return [];
    
    const links = [];
    // Match [[Note Title]] or [[Note Title|Alias]]
    const wikiLinkRegex = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g;
    
    let match;
    while ((match = wikiLinkRegex.exec(content)) !== null) {
      links.push({
        title: match[1].trim(),
        alias: match[2] ? match[2].trim() : null,
        raw: match[0],
        index: match.index
      });
    }
    
    return links;
  }
  
  /**
   * Extract tags from content
   * @param {string} content - Markdown content
   * @returns {Array} Array of tag strings (without #)
   */
  extractTags(content): any {
    if (!content) return [];
    
    const tags = new Set();
    // Match #tag or #multi-word-tag (alphanumeric, hyphens, underscores)
    const tagRegex = /#([\w-]+)/g;
    
    let match;
    while ((match = tagRegex.exec(content)) !== null) {
      tags.add(match[1]);
    }
    
    return Array.from(tags);
  }
  
  /**
   * Convert wiki links to HTML links
   * @param {string} content - Markdown content
   * @param {Function} onLinkClick - Callback for link clicks
   * @returns {string} HTML with clickable links
   */
  renderWikiLinks(content, onLinkClick): any {
    if (!content) return '';
    
    return content.replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (match, title, alias) => {
      const displayText = alias || title;
      const noteExists = this.noteExists(title.trim());
      const className = noteExists ? 'wiki-link' : 'wiki-link-broken';
      
      return `<a href="#" class="${className}" data-note-title="${title.trim()}">${displayText}</a>`;
    });
  }
  
  /**
   * Check if a note with the given title exists
   * @param {string} title - Note title
   * @returns {boolean}
   */
  noteExists(title): any {
    return this.items.some(item => {
      const name = this.getField(item, 'name');
      return name === title;
    });
  }
  
  /**
   * Get all notes that link to a specific note
   * @param {string} noteTitle - Title of the target note
   * @returns {Array} Array of items that link to this note
   */
  getBacklinks(noteTitle): string {
    return this.items.filter(item => {
      const content = this.getField(item, 'content') || '';
      const links = this.parseLinks(content);
      return links.some(link => link.title === noteTitle);
    });
  }
  
  /**
   * Get all notes with a specific tag
   * @param {string} tag - Tag to search for (without #)
   * @returns {Array} Array of items with this tag
   */
  getNotesWithTag(tag): string {
    return this.items.filter(item => {
      const content = this.getField(item, 'content') || '';
      const tags = this.extractTags(content);
      return tags.includes(tag);
    });
  }
  
  /**
   * Build a graph of note connections
   * @returns {Object} Graph with nodes and edges
   */
  buildLinkGraph(): any {
    const nodes = [];
    const edges = [];
    
    this.items.forEach(item => {
      const id = item.id;
      const name = this.getField(item, 'name') || 'Untitled';
      const content = this.getField(item, 'content') || '';
      const links = this.parseLinks(content);
      
      // Add node
      nodes.push({
        id,
        label: name,
        tags: this.extractTags(content)
      });
      
      // Add edges for each link
      links.forEach(link => {
        const targetItem = this.items.find(i => 
          this.getField(i, 'name') === link.title
        );
        
        if (targetItem) {
          edges.push({
            from: id,
            to: targetItem.id,
            label: link.alias || link.title
          });
        }
      });
    });
    
    return { nodes, edges };
  }
  
  /**
   * Suggest note titles for autocomplete
   * @param {string} query - Partial note title
   * @returns {Array} Array of matching note titles
   */
  suggestNotes(query): any {
    if (!query) return [];
    
    const lowerQuery = query.toLowerCase();
    return this.items
      .map(item => this.getField(item, 'name'))
      .filter(name => name && name.toLowerCase().includes(lowerQuery))
      .sort();
  }
  
  /**
   * Parse frontmatter (YAML metadata at top of note)
   * @param {string} content - Note content
   * @returns {Object} Parsed frontmatter or null
   */
  parseFrontmatter(content): any {
    if (!content) return null;
    
    // Check if content starts with ---
    if (!content.startsWith('---\n')) return null;
    
    // Find closing ---
    const endIndex = content.indexOf('\n---\n', 4);
    if (endIndex === -1) return null;
    
    const frontmatterText = content.substring(4, endIndex);
    
    try {
      // Simple YAML parser (for basic key: value pairs)
      const frontmatter = {};
      frontmatterText.split('\n').forEach(line => {
        const colonIndex = line.indexOf(':');
        if (colonIndex > 0) {
          const key = line.substring(0, colonIndex).trim();
          const value = line.substring(colonIndex + 1).trim();
          frontmatter[key] = value;
        }
      });
      
      return frontmatter;
    } catch (error) {
      console.error('Error parsing frontmatter:', error);
      return null;
    }
  }
  
  /**
   * Remove frontmatter from content
   * @param {string} content - Note content
   * @returns {string} Content without frontmatter
   */
  removeFrontmatter(content): any {
    if (!content || !content.startsWith('---\n')) return content;
    
    const endIndex = content.indexOf('\n---\n', 4);
    if (endIndex === -1) return content;
    
    return content.substring(endIndex + 5).trim();
  }
}

export default WikiLinkParser;
